import { useTranslation } from 'react-i18next'

import { inputId } from 'aem-libs-tests/src/constants/inputId'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'

import { CommonInputWrapper } from '../CommonInputWrapper'
import { NumberField, NumberFieldProps } from '../number-field'
import { ValidationMessage } from '../validation-message'
import * as PlusMinusFieldStyled from './PlusMinusNumberField.styled'
import cs from './i18n/cs.json'
import en from './i18n/en.json'
import { usePlusMinusNumberField } from './usePlusMinusNumberField'


export type ChangeCompleteHandler = (value: number) => void

export interface PlusMinusNumberFieldProps extends Omit<NumberFieldProps, 'onChangeComplete' | 'value'> {
  step?: number
  value: number
  onPlus?: () => void
  onMinus?: () => void
  onChangeComplete?: ChangeCompleteHandler
}

export const PlusMinusNumberField = ({
  step,
  onMinus,
  onPlus,
  hideValidation,
  label,
  tip,
  testId,
  onChangeComplete,
  value,
  ...numberFieldProps
}: PlusMinusNumberFieldProps) => {
  const { t, i18n } = useTranslation('PlusMinusNumberField')
  i18n.addResourceBundle('cs', 'PlusMinusNumberField', cs)
  i18n.addResourceBundle('en', 'PlusMinusNumberField', en)

  const { handlePlusClick, handleMinusClick, delayedValue, handleNumberFieldChange } = usePlusMinusNumberField({
    step,
    onMinus,
    onPlus,
    label,
    onChangeComplete,
    value,
    ...numberFieldProps
  })
  const usedTestId = testId ?? 'plus-minus-numberfield'

  return (
    <CommonInputWrapper
      htmlFor={numberFieldProps.id}
      label={label}
      tip={tip}
      testId={createTestId(usedTestId, inputId.plusMinus)}
    >
      <PlusMinusFieldStyled.Container $responsive={numberFieldProps.responsive}>
        <PlusMinusFieldStyled.Inner>
          <PlusMinusFieldStyled.Button
            id='button-minus'
            type={'button'}
            onClick={handleMinusClick}
            disabled={numberFieldProps.disabled}
            data-testid={createTestId(usedTestId, inputId.minus)}
            aria-describedby={numberFieldProps.id + '-label'}
            aria-label={t('PLUS_MINUS_NUMBER_FIELD.DECREASE')}
            tabIndex={numberFieldProps.min ? (value <= numberFieldProps.min ? -1 : 0) : 0}
          />
          <NumberField
            {...numberFieldProps}
            value={delayedValue ?? value}
            mode={'default'}
            testId={usedTestId}
            onChangeComplete={handleNumberFieldChange}
            hideValidation
          />
          <PlusMinusFieldStyled.Button
            id='button-plus'
            type={'button'}
            onClick={handlePlusClick}
            disabled={numberFieldProps.disabled}
            data-testid={createTestId(usedTestId, inputId.plus)}
            aria-describedby={numberFieldProps.id + '-label'}
            aria-label={t('PLUS_MINUS_NUMBER_FIELD.INCREASE')}
            tabIndex={numberFieldProps.max ? (value >= numberFieldProps.max ? -1 : 0) : 0}
          />
        </PlusMinusFieldStyled.Inner>
        {!hideValidation && numberFieldProps.validationMessage && (
          <ValidationMessage message={numberFieldProps.validationMessage} testId={usedTestId} />
        )}
      </PlusMinusFieldStyled.Container>
    </CommonInputWrapper>
  )
}
